import { CustomError, ErrorCode } from 'src/error';
import { Country } from 'src/types/enums';

const VAT_NUMBER_REGEXES: { [key: string]: RegExp } = {
  AT: /^(AT)?U[0-9]{8}$/,
  BA: /^(BA)?[0-9]{12}$/,
  BE: /^(BE)?[0-9]{10}$/,
  BG: /^(BG)?[0-9]{9,10}$/,
  CH: /^(CH)?E[0-9]{9}(MWST|TVA|IVA)?$/,
  CY: /^(CY)?[0-9]{8}[A-Z]$/,
  CZ: /^(CZ)?[0-9]{8,10}$/,
  DE: /^(DE)?[0-9]{9}$/,
  DK: /^(DK)?[0-9]{8}$/,
  EE: /^(EE)?[0-9]{9}$/,
  EL: /^(EL|GR)?[0-9]{9}$/,
  GR: /^(EL|GR)?[0-9]{9}$/,
  ES: /^(ES)?[0-9A-Z][0-9]{7}[0-9A-Z]$/,
  FI: /^(FI)?[0-9]{8}$/,
  FR: /^(FR)?[0-9A-Z]{2}[0-9]{9}$/,
  GB: /^(GB)?([0-9]{9}([0-9]{3})?|[A-Z]{2}[0-9]{3})$/,
  HU: /^(HU)?[0-9]{8}$/,
  IE: /^(IE)?([0-9]{7}[A-Z]{1,2}|[0-9]{9}[A-Z]{2})$/,
  IT: /^(IT)?[0-9]{11}$/,
  LT: /^(LT)?([0-9]{9}|[0-9]{12})$/,
  LU: /^(LU)?[0-9]{8}$/,
  LV: /^(LV)?[0-9]{11}$/,
  MK: /^(MK)?[0-9]{13}$/,
  MT: /^(MT)?[0-9]{8}$/,
  NL: /^(NL)?[0-9]{9}B[0-9]{2}$/,
  NO: /^(NO)?[0-9]{9}MVA?$/,
  PL: /^(PL)?[0-9]{10}$/,
  PT: /^(PT)?[0-9]{9}$/,
  RO: /^(RO)?[0-9]{2,10}$/,
  SE: /^(SE)?[0-9]{12}$/,
  SI: /^(SI)?[0-9]{8}$/,
  SK: /^(SK)?[0-9]{10}$/,
  HR: /^(HR)?[0-9]{11}$/,
  RS: /^(RS)?[0-9]{9}$/,
};

const VAT_NUMBER_FORMATS = new Map<string, number[]>([
  ['BE', [4, 3, 3]],
  ['NL', [4, 2, 3, 3]],
]);

const NON_EU_COUNTRY_CODES = new Set<string>([Country.Ba, Country.Ch, Country.Gb, Country.Mk, Country.No, Country.Rs]);

/**
 * Cleans a company number by removing spaces and dots, and converting it to uppercase.
 */
export function cleanCompanyNumber(val: string) {
  return val.replace(/[\s.-]+/g, '').toUpperCase();
}

function isNonEuCountry(countryCode: string): boolean {
  return NON_EU_COUNTRY_CODES.has(countryCode.toUpperCase());
}

function getCountryCodeFromValue(cleanedVatNumber: string): string | undefined {
  if (cleanedVatNumber.startsWith('CHE')) {
    return 'CH';
  }

  const countryCode = cleanedVatNumber.substring(0, 2);
  if (VAT_NUMBER_REGEXES[countryCode]) {
    return countryCode;
  }

  return undefined;
}

function matchesCountryVatPattern(cleanedVatNumber: string, countryCode: string): boolean {
  const countryRegex = VAT_NUMBER_REGEXES[countryCode];
  if (!countryRegex) {
    return false;
  }

  if (!countryRegex.test(cleanedVatNumber)) {
    return false;
  }

  if (countryCode === 'BE' && !validateBE(cleanedVatNumber.substring(2))) {
    return false;
  }

  return true;
}

function matchesNonEuPermissivePattern(cleanedVatNumber: string, countryCode: string): boolean {
  const upperCountry = countryCode.toUpperCase();
  const withoutPrefix = cleanedVatNumber.startsWith(upperCountry)
    ? cleanedVatNumber.substring(upperCountry.length)
    : cleanedVatNumber;

  return /^[A-Z0-9]{4,18}$/.test(withoutPrefix);
}

function candidateVatNumbers(cleanedVatNumber: string, country?: string): string[] {
  const candidates = new Set<string>([cleanedVatNumber]);
  const countryCode = country?.toUpperCase();

  if (!countryCode) {
    return [...candidates];
  }

  if (!cleanedVatNumber.startsWith(countryCode)) {
    candidates.add(`${countryCode}${cleanedVatNumber}`);
  }

  if (countryCode === 'CH') {
    if (cleanedVatNumber.startsWith('E')) {
      candidates.add(`CHE${cleanedVatNumber}`);
      candidates.add(`CH${cleanedVatNumber}`);
    } else if (!cleanedVatNumber.startsWith('CHE') && !cleanedVatNumber.startsWith('CH')) {
      candidates.add(`CHE${cleanedVatNumber}`);
      candidates.add(`CH${cleanedVatNumber}`);
    }
  }

  if (countryCode === 'NO') {
    for (const c of [...candidates]) {
      if (!c.endsWith('MVA') && !c.endsWith('MV')) {
        candidates.add(`${c}MVA`);
      }
    }
  }

  return [...candidates];
}

/**
 * Validates a VAT number based on its country code.
 * When `country` is provided, numbers without a country prefix are also accepted.
 */
export function isValidVatNumber(val: string, country?: string): boolean {
  const cleanedVatNumber = cleanCompanyNumber(val);
  if (cleanedVatNumber.length < 2) {
    console.error('Invalid VAT number: too short');
    return false;
  }

  for (const candidate of candidateVatNumbers(cleanedVatNumber, country)) {
    const countryCode = getCountryCodeFromValue(candidate);
    if (!countryCode) {
      continue;
    }

    if (matchesCountryVatPattern(candidate, countryCode)) {
      return true;
    }
  }

  const hintedCountry = country?.toUpperCase();
  if (
    hintedCountry &&
    isNonEuCountry(hintedCountry) &&
    matchesNonEuPermissivePattern(cleanedVatNumber, hintedCountry)
  ) {
    return true;
  }

  const countryCode = getCountryCodeFromValue(cleanedVatNumber);
  if (!countryCode) {
    console.error(`Invalid VAT number: unsupported country code ${cleanedVatNumber.substring(0, 2)}`);
    return false;
  }

  return false;
}

/**
 * Validates an ondernemingsnummer / company registration number.
 * Accepts VAT-formatted values and local formats (e.g. Belgian numbers without BE prefix).
 */
export function isValidCompanyNumber(val: string, country?: string): boolean {
  if (!val || val.trim().length === 0) {
    return false;
  }

  if (isValidVatNumber(val, country)) {
    return true;
  }

  const cleaned = cleanCompanyNumber(val);
  const countryCode = country?.toUpperCase();

  if ((!countryCode || countryCode === 'BE') && /^[0-1][0-9]{9}$/.test(cleaned)) {
    return validateBE(cleaned);
  }

  if (countryCode && isNonEuCountry(countryCode)) {
    return matchesNonEuPermissivePattern(cleaned, countryCode);
  }

  return /^[A-Z0-9]{4,20}$/.test(cleaned);
}

function normalizeVatNumber(cleanedVatNumber: string, country?: string): string {
  for (const candidate of candidateVatNumbers(cleanedVatNumber, country)) {
    const countryCode = getCountryCodeFromValue(candidate);
    if (countryCode && matchesCountryVatPattern(candidate, countryCode)) {
      return candidate;
    }
  }

  const countryCode = country?.toUpperCase();
  if (countryCode && isNonEuCountry(countryCode) && matchesNonEuPermissivePattern(cleanedVatNumber, countryCode)) {
    return cleanedVatNumber.startsWith(countryCode) ? cleanedVatNumber : `${countryCode}${cleanedVatNumber}`;
  }

  return cleanedVatNumber;
}

export function cleanAndValidateVatNumber(val: string, country?: string): string {
  const cleaned = cleanCompanyNumber(val);
  if (!isValidVatNumber(val, country)) {
    throw new CustomError('Invalid vat number', ErrorCode.VALIDATION_ERROR);
  }
  return normalizeVatNumber(cleaned, country);
}

export function formatVatNumber(val: string): string {
  const cleaned = cleanCompanyNumber(val);
  const countryCode = cleaned.substring(0, 2);
  let result = countryCode + ' ';
  const format = VAT_NUMBER_FORMATS.get(countryCode) ?? [];
  let offset = 2;
  for (let i = 0; i < format.length; i++) {
    const group = format[i];
    result += cleaned.substring(offset, offset + group);
    if (i < format.length - 1) {
      result += '.';
    }
    offset += group;
  }
  // If any characters are left, we also should print it
  // this function does no validation
  // also handles countries that we didn't implement yet
  result += cleaned.substring(offset);
  return result;
}

/**
 * Belgium (BE): Modulo 97 check
 * Format: BE 0XXX.XXX.XXX or BE 1XXX.XXX.XXX
 * Check: 97 - (first 8 digits MOD 97) = last 2 digits
 */
function validateBE(vatDigits: string): boolean {
  if (vatDigits.length !== 10) return false;
  const base = parseInt(vatDigits.substring(0, 8), 10);
  const checkDigits = parseInt(vatDigits.substring(8, 10), 10);
  const calculated = 97 - (base % 97);
  return calculated === checkDigits;
}
